'use client';

import { useState, useCallback, useEffect } from 'react';
import {
  LegacyDialog,
  Button,
  Text,
  IconButton,
  Spinner,
  Input,
  Progress,
  Select,
  createSelectListCollection,
} from '@terra/ui-design-system';
import { Box, VStack, HStack } from '@terra/ui-styled-system';
import { AlertCircleIcon, Tick01Icon, Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import {
  useReportGeneration,
  type ReportGenerationJob,
} from '../../hooks/useReportGeneration';

type CreateReportDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFindingsCount: number;
  selectedFindingIds: string[];
  applicationId: string;
  onComplete: (reportId: string) => void;
  onAddToExisting: (reportId: string) => Promise<boolean>;
  existingReports?: ReportsReturnDto[];
  isLoadingReports?: boolean;
  canCreateNew?: boolean;
  createNewDisabledReason?: string;
};
const getProgressColor = (
  status: ReportGenerationJob['status'] | undefined,
) => {
  switch (status) {
    case 'COMPLETED':
      return 'green';
    case 'FAILED':
      return 'red';
    default:
      return 'blue';
  }
};

export function CreateReportDialog({
  isOpen,
  onOpenChange,
  selectedFindingsCount,
  selectedFindingIds,
  applicationId,
  onComplete,
  onAddToExisting,
  existingReports = [],
  isLoadingReports = false,
  canCreateNew = true,
  createNewDisabledReason,
}: CreateReportDialogProps) {
  const t = useTranslations('proxy.findings.createReportDialog');
  const { currentJob, isGenerating, generateReport, resetJob } =
    useReportGeneration();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [customTitle, setCustomTitle] = useState<string>('');

  // Handle completion
  useEffect(() => {
    const reportId = currentJob?.reportId;

    if (currentJob?.status === 'COMPLETED' && reportId) {
      // Small delay to show completion state
      const timer = setTimeout(() => {
        onComplete(reportId);
        onOpenChange(false);
        resetJob();
        setCustomTitle('');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [
    currentJob?.status,
    currentJob?.reportId,
    onComplete,
    onOpenChange,
    resetJob,
  ]);
  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReportId('');
      setCustomTitle('');
      if (!isGenerating) {
        resetJob();
      }
    }
  }, [isOpen, isGenerating, resetJob]);
  const handleCreateNew = useCallback(async () => {
    await generateReport({
      applicationId,
      findingIds: selectedFindingIds,
      title: customTitle || undefined,
      isNewReport: true,
    });
  }, [generateReport, applicationId, selectedFindingIds, customTitle]);
  const handleAddToExisting = useCallback(async () => {
    if (!selectedReportId) return;
    setIsSubmitting(true);

    try {
      const added = await onAddToExisting(selectedReportId);

      if (added) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Failed to add findings to report:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedReportId, onAddToExisting, onOpenChange]);
  const hasExistingReports = existingReports.length > 0;
  const showProgress = isGenerating || currentJob;
  // Create collection for Select component
  const reportsCollection = createSelectListCollection({
    items: existingReports.map((report) => ({
      value: report.id,
      label: report.title || `Report ${report.id.slice(0, 8)}...`,
    })),
  });

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        // Don't allow closing while generating
        if (!isGenerating) {
          onOpenChange(open);
        }
      }}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="md" w="full">
          <VStack gap="4" p="4">
            {/* Title & Description */}
            <VStack gap="1" w="full" alignItems="flex-start">
              <LegacyDialog.Title>Generate Report</LegacyDialog.Title>
              <LegacyDialog.Description color="fg.muted">
                {showProgress
                  ? 'Generating your report with AI enrichment...'
                  : `Choose how to proceed with ${selectedFindingsCount} selected finding${selectedFindingsCount !== 1 ? 's' : ''}`}
              </LegacyDialog.Description>
            </VStack>

            {/* Content */}
            {showProgress ? (
              <VStack gap="3" w="full">
                <VStack gap="1" w="full">
                  <HStack justifyContent="space-between" w="full">
                    <Text fontSize="xs">
                      {currentJob?.message || 'Processing...'}
                    </Text>
                    <Text fontSize="xs" color="fg.muted">
                      {currentJob?.progress ?? 0}%
                    </Text>
                  </HStack>
                  <Progress
                    value={currentJob?.progress ?? 0}
                    max={100}
                    size="sm"
                    colorPalette={getProgressColor(currentJob?.status)}
                  />
                </VStack>

                {currentJob?.status === 'FAILED' && (
                  <HStack
                    gap="2"
                    p="2"
                    bg="bg.subtle"
                    borderRadius="md"
                    w="full"
                  >
                    <AlertCircleIcon size={14} />
                    <Text fontSize="xs" color="fg.error">
                      {currentJob.error || 'Generation failed'}
                    </Text>
                  </HStack>
                )}

                {currentJob?.status === 'COMPLETED' && (
                  <HStack
                    gap="2"
                    p="2"
                    bg="green.subtle"
                    borderRadius="md"
                    w="full"
                  >
                    <Tick01Icon size={14} />
                    <Text fontSize="xs" color="green.fg">
                      Report generated successfully! Redirecting...
                    </Text>
                  </HStack>
                )}
              </VStack>
            ) : isLoadingReports ? (
              <Box display="flex" justifyContent="center" py="3">
                <Spinner size="sm" />
              </Box>
            ) : (
              <VStack gap="3" w="full">
                {canCreateNew ? (
                  <VStack gap="1" w="full" alignItems="stretch">
                    <Text fontSize="xs" fontWeight="medium">
                      Report Title (optional)
                    </Text>
                    <Input
                      placeholder={`Report - ${new Date().toLocaleDateString()}`}
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      size="sm"
                    />
                  </VStack>
                ) : (
                  <HStack
                    gap="2"
                    p="2"
                    bg="bg.subtle"
                    borderRadius="md"
                    w="full"
                    alignItems="flex-start"
                  >
                    <AlertCircleIcon size={14} />
                    <Text fontSize="xs" color="fg.muted">
                      {createNewDisabledReason ??
                        t('createNewDisabledFallback')}
                    </Text>
                  </HStack>
                )}

                {hasExistingReports && (
                  <VStack gap="1" w="full" alignItems="stretch">
                    <Text fontSize="xs" fontWeight="medium">
                      {canCreateNew
                        ? t('orAddToExistingReport')
                        : t('addToExistingReport')}
                    </Text>
                    <Select.Root
                      positioning={{ sameWidth: true, strategy: 'fixed' }}
                      width="full"
                      size="sm"
                      value={selectedReportId ? [selectedReportId] : []}
                      collection={reportsCollection}
                      onValueChange={(e) =>
                        setSelectedReportId(e.value[0] || '')
                      }
                    >
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Select a report..." />
                          <Select.Indicator />
                        </Select.Trigger>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          <Select.ItemGroup>
                            {reportsCollection.items.map((item) => (
                              <Select.Item key={item.value} item={item}>
                                <Select.ItemText>{item.label}</Select.ItemText>
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.ItemGroup>
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  </VStack>
                )}
              </VStack>
            )}

            {/* Footer Buttons */}
            {!showProgress && (
              <HStack gap="2" justifyContent="flex-end" w="full">
                <LegacyDialog.CloseTrigger asChild>
                  <Button variant="outline" size="xs" disabled={isSubmitting}>
                    Cancel
                  </Button>
                </LegacyDialog.CloseTrigger>
                {selectedReportId ? (
                  <Button
                    size="xs"
                    onClick={handleAddToExisting}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    Add to Report
                  </Button>
                ) : canCreateNew ? (
                  <Button
                    size="xs"
                    onClick={handleCreateNew}
                    disabled={isSubmitting || selectedFindingIds.length === 0}
                  >
                    Create Report
                  </Button>
                ) : null}
              </HStack>
            )}

            {/* Show retry button on failure */}
            {currentJob?.status === 'FAILED' && (
              <HStack gap="2" justifyContent="flex-end" w="full">
                <Button variant="outline" size="xs" onClick={() => resetJob()}>
                  Try Again
                </Button>
                <LegacyDialog.CloseTrigger asChild>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => resetJob()}
                  >
                    Close
                  </Button>
                </LegacyDialog.CloseTrigger>
              </HStack>
            )}
          </VStack>

          {/* Close Button - only show when not generating */}
          {!isGenerating && (
            <LegacyDialog.CloseTrigger
              asChild
              position="absolute"
              top="2"
              right="2"
            >
              <IconButton
                aria-label="Close Dialog"
                variant="ghost"
                size="xs"
                disabled={isSubmitting}
              >
                <Cancel01Icon size={14} />
              </IconButton>
            </LegacyDialog.CloseTrigger>
          )}
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
